import { Injectable, inject } from '@angular/core';
import {
  ExpenditureRateEntry,
  ProjectExpenditureEntryModel,
} from '../../../../../../../core/models/project/project-expenditure-entry.model';
import { assert } from '../../../../../../../core/assert/assert';
import { DateTimeHelper } from '../../../../../../../features/calendar/util/date-time-helper';
import { ProjectModel } from '../../../../../../../core/models/project/project.model';
import { ExpenditureHistoryDialogComponent } from '../../../dialogs/expenditure-history-dialog/expenditure-history-dialog.component';
import { filter } from 'rxjs/operators';
import { UserOperation } from '../../../../../../../core/enums/user-operation';
import { ShiveDialogService } from '../../../../../../../core/services/controls/shive-dialog.service';
import { ProjectDataService } from '../../../../state-and-data-handling/project-data.service';

@Injectable({
  providedIn: 'root',
})
export class ExpenditureEntryService {
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly projectDataService = inject(ProjectDataService);

  public createExpenditureEntryModel(
    expenditureValue: number,
    startingFrom?: Date,
    id?: number,
    type?: 'memberId' | 'taskId',
  ): ProjectExpenditureEntryModel {
    const expenditureEntry = new ProjectExpenditureEntryModel();
    expenditureEntry.expenditureValue = expenditureValue;
    expenditureEntry.startingFrom = startingFrom ?? null;
    if (type) {
      expenditureEntry[type] = id;
    }
    return expenditureEntry;
  }

  public createExpenditureEntry(
    expenditureRate: number,
    startingFrom: Date,
    id: number,
  ): ExpenditureRateEntry {
    return { startingFrom, rate: expenditureRate, id };
  }

  /**
   * Add newExpenditureEntry to the list of expenditure entries. If the new expenditure entry is before an existing expenditure entry, then
   * all subsequent expenditure entries will be deleted.
   */
  public alignExpenditureEntriesByDateFrom(
    newExpenditureEntry: ExpenditureRateEntry,
    currentEntries: readonly ExpenditureRateEntry[],
  ): ExpenditureRateEntry[] {
    assert(newExpenditureEntry.startingFrom !== null);
    assert(currentEntries.length > 0);

    const updatedEntries: ExpenditureRateEntry[] = [];
    for (const entry of currentEntries) {
      const differenceInDays = DateTimeHelper.differenceInDays(
        entry.startingFrom,
        newExpenditureEntry.startingFrom,
      );
      if (differenceInDays >= 0) {
        break;
      } else {
        updatedEntries.push(entry);
      }
    }
    updatedEntries.push(newExpenditureEntry);

    return updatedEntries;
  }

  // todo martin sort? make a test
  public getEntriesForTask(
    project: ProjectModel,
    taskId: number,
  ): readonly ProjectExpenditureEntryModel[] {
    return project.projectExpenditureEntries.filter((entry) => entry.taskId === taskId);
  }

  // todo martin sort? make a test
  public getEntriesForMember(
    project: ProjectModel,
    memberId: number,
  ): readonly ProjectExpenditureEntryModel[] {
    return project.projectExpenditureEntries.filter((entry) => entry.memberId === memberId);
  }

  // todo martin sort? make a test
  public getEntriesFixed(project: ProjectModel): readonly ProjectExpenditureEntryModel[] {
    return project.projectExpenditureEntries;
  }

  public openNewExpenditureRateDialog(
    initialHistory: ExpenditureRateEntry[],
    applyResult: (entries: ExpenditureRateEntry[]) => void,
  ): void {
    const ref = this.shiveDialogService.open({
      title: 'Verrechenbarer Stundensatz',
      content: ExpenditureHistoryDialogComponent,
      width: 572,
      offsetX: 80,
    });

    const dialogComponent = ref.content.instance as ExpenditureHistoryDialogComponent;
    dialogComponent.expenditureRateHistory = initialHistory;

    ref.result
      .pipe(filter((dialogResult) => dialogResult !== UserOperation.Cancel))
      .subscribe((dialogResult) => {
        applyResult(dialogResult as ExpenditureRateEntry[]);
        this.projectDataService.userOperationConducted$.next(true);
      });
  }
}
